import type { Inventory, InventoryBatch, InventoryMovement } from '../types/pz';
import { READABLE_QUANTITY_UNITS, type QuantityUnit } from '../utils/commercialQuantity';
import {
  asNumber,
  asOptionalArray,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';

export interface InventoryVariantDetailDto {
  batches: InventoryBatch[];
  recentMovements: InventoryMovement[];
  batchesLoaded: boolean;
  recentMovementsLoaded: boolean;
}

export interface QuantityReportBucket extends Record<string, unknown> {
  quantity: number;
  quantityUnit?: QuantityUnit | null;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  valuePLN: number;
  count?: number;
}

export interface InventoryVariantReport {
  productVariantId: string;
  sku?: string | null;
  productName?: string | null;
  variantName?: string | null;
  opening: QuantityReportBucket;
  receipts: QuantityReportBucket;
  releases: QuantityReportBucket;
  adjustments: QuantityReportBucket;
  closing: QuantityReportBucket;
}

export interface MonthlyInventoryReport {
  year: number;
  month: number;
  periodStart?: string;
  periodEnd?: string;
  summary: {
    openingBalancePLN: number;
    receiptsPLN: number;
    releasesPLN: number;
    adjustmentsPLN: number;
    closingBalancePLN: number;
    variantsCount?: number;
  };
  variants: InventoryVariantReport[];
}

export type InventoryStockStatus = NonNullable<Inventory['stockStatus']>;

const INVENTORY_STOCK_STATUSES: readonly InventoryStockStatus[] = [
  'IN_STOCK',
  'LOW_STOCK',
  'OUT_OF_STOCK',
];

export function getInventoryStockStatus(
  inventory: Pick<Inventory, 'stockStatus' | 'quantityAvailable' | 'quantityAvailablePcs' | 'quantityAvailableM2' | 'quantityAvailableM3'> | null | undefined,
): InventoryStockStatus {
  if (INVENTORY_STOCK_STATUSES.includes(inventory?.stockStatus as InventoryStockStatus)) {
    return inventory?.stockStatus as InventoryStockStatus;
  }

  // Compatibility only for responses from older deployments. The backend is
  // the canonical owner once `stockStatus` is present.
  const atomicValues = [
    inventory?.quantityAvailablePcs,
    inventory?.quantityAvailableM2,
    inventory?.quantityAvailableM3,
  ].flatMap((value) => value === null || value === undefined ? [] : [Number(value)]);
  const available = atomicValues.length > 0
    ? atomicValues.some((value) => value > 0)
    : Number(inventory?.quantityAvailable || 0) > 0;
  return available ? 'IN_STOCK' : 'OUT_OF_STOCK';
}

export const isInventoryAvailable = (inventory: Parameters<typeof getInventoryStockStatus>[0]): boolean => (
  getInventoryStockStatus(inventory) !== 'OUT_OF_STOCK'
);

export function normalizeInventory(raw: unknown, entity = 'inventory'): Inventory {
  const record = asRecord(raw);
  if (!record) reportDtoAnomaly(entity, 'root', raw);
  const variant = asRecord(record?.productVariant);
  const product = asRecord(variant?.product);
  const quantityUnit = normalizeOptionalQuantityUnit(record?.quantityUnit, entity);
  const stockStatus = INVENTORY_STOCK_STATUSES.includes(record?.stockStatus as InventoryStockStatus)
    ? record?.stockStatus as InventoryStockStatus
    : getInventoryStockStatus({
      quantityAvailable: asNumber(record?.quantityAvailable),
      quantityAvailablePcs: record?.quantityAvailablePcs == null ? null : asNumber(record.quantityAvailablePcs),
      quantityAvailableM2: record?.quantityAvailableM2 == null ? null : asNumber(record.quantityAvailableM2),
      quantityAvailableM3: record?.quantityAvailableM3 == null ? null : asNumber(record.quantityAvailableM3),
    });

  return {
    id: asString(record?.id),
    productVariantId: asString(record?.productVariantId),
    ...(quantityUnit ? { quantityUnit } : {}),
    quantityAvailable: asNumber(record?.quantityAvailable),
    ...(record?.quantityAvailablePcs == null ? {} : { quantityAvailablePcs: asNumber(record.quantityAvailablePcs) }),
    ...(record?.quantityAvailableM2 == null ? {} : { quantityAvailableM2: asNumber(record.quantityAvailableM2) }),
    ...(record?.quantityAvailableM3 == null ? {} : { quantityAvailableM3: asNumber(record.quantityAvailableM3) }),
    quantityInTransit: asNumber(record?.quantityInTransit),
    ...(record?.quantityInTransitPcs == null ? {} : { quantityInTransitPcs: asNumber(record.quantityInTransitPcs) }),
    ...(record?.quantityInTransitM2 == null ? {} : { quantityInTransitM2: asNumber(record.quantityInTransitM2) }),
    ...(record?.quantityInTransitM3 == null ? {} : { quantityInTransitM3: asNumber(record.quantityInTransitM3) }),
    quantityReserved: asNumber(record?.quantityReserved),
    ...(record?.quantityReservedPcs == null ? {} : { quantityReservedPcs: asNumber(record.quantityReservedPcs) }),
    ...(record?.quantityReservedM2 == null ? {} : { quantityReservedM2: asNumber(record.quantityReservedM2) }),
    ...(record?.quantityReservedM3 == null ? {} : { quantityReservedM3: asNumber(record.quantityReservedM3) }),
    quantityTotal: asNumber(record?.quantityTotal),
    ...(record?.quantityTotalPcs == null ? {} : { quantityTotalPcs: asNumber(record.quantityTotalPcs) }),
    ...(record?.quantityTotalM2 == null ? {} : { quantityTotalM2: asNumber(record.quantityTotalM2) }),
    ...(record?.quantityTotalM3 == null ? {} : { quantityTotalM3: asNumber(record.quantityTotalM3) }),
    ...(record?.averageCostPLN == null ? {} : { averageCostPLN: asNumber(record.averageCostPLN) }),
    ...(record?.totalValuePLN == null ? {} : { totalValuePLN: asNumber(record.totalValuePLN) }),
    status: asString(record?.status),
    stockStatus,
    ...(asOptionalString(record?.lastMovementAt) ? { lastMovementAt: asOptionalString(record?.lastMovementAt) } : {}),
    ...(asOptionalString(record?.lastMovementType) ? { lastMovementType: asOptionalString(record?.lastMovementType) } : {}),
    ...(variant ? {
      productVariant: {
        id: asString(variant.id),
        sku: asString(variant.sku),
        variantName: asString(variant.variantName),
        ...(variant.minStockLevelPcs == null ? {} : { minStockLevelPcs: asNumber(variant.minStockLevelPcs) }),
        ...(variant.minStockLevelM2 == null ? {} : { minStockLevelM2: asNumber(variant.minStockLevelM2) }),
        ...(variant.minStockLevelM3 == null ? {} : { minStockLevelM3: asNumber(variant.minStockLevelM3) }),
        ...(variant.maxStockLevelPcs == null ? {} : { maxStockLevelPcs: asNumber(variant.maxStockLevelPcs) }),
        ...(variant.maxStockLevelM2 == null ? {} : { maxStockLevelM2: asNumber(variant.maxStockLevelM2) }),
        ...(variant.maxStockLevelM3 == null ? {} : { maxStockLevelM3: asNumber(variant.maxStockLevelM3) }),
        ...(product ? {
          product: {
            id: asString(product.id),
            code: asString(product.code),
            name: asString(product.name),
            ...(product.width === undefined ? {} : { width: product.width === null ? null : asNumber(product.width) }),
            ...(product.length === undefined ? {} : { length: product.length === null ? null : asNumber(product.length) }),
            ...(product.height === undefined ? {} : { height: product.height === null ? null : asNumber(product.height) }),
            ...(product.thickness === undefined ? {} : { thickness: product.thickness === null ? null : asNumber(product.thickness) }),
            ...(asOptionalString(product.category) ? { category: asOptionalString(product.category) } : {}),
            productCategoryId: asOptionalString(product.productCategoryId) ?? null,
            productCategoryName: asOptionalString(product.productCategoryName) ?? null,
            productCategoryColor: asOptionalString(product.productCategoryColor) ?? null,
          },
        } : {}),
      },
    } : {}),
  };
}

const BATCH_STATUSES: InventoryBatch['status'][] = ['ACTIVE', 'DEPLETED', 'EXPIRED'];
const MOVEMENT_TYPES: InventoryMovement['movementType'][] = [
  'PZ_IN',
  'WZ_OUT',
  'ADJUSTMENT_IN',
  'ADJUSTMENT_OUT',
  'DAMAGE',
  'RETURN',
  'TRANSFER',
  'INVENTORY_COUNT',
  'ADJUSTMENT',
];

const normalizeOptionalQuantityUnit = (value: unknown, entity: string): QuantityUnit | undefined => {
  if (value == null || value === '') return undefined;
  if ((READABLE_QUANTITY_UNITS as readonly unknown[]).includes(value)) return value as QuantityUnit;
  reportDtoAnomaly(entity, 'quantityUnit', value);
  return undefined;
};

const normalizeBatchStatus = (value: unknown, entity: string): InventoryBatch['status'] => {
  if (BATCH_STATUSES.includes(value as InventoryBatch['status'])) {
    return value as InventoryBatch['status'];
  }
  reportDtoAnomaly(entity, 'status', value);
  return 'ACTIVE';
};

const normalizeMovementType = (value: unknown, entity: string): InventoryMovement['movementType'] => {
  if (MOVEMENT_TYPES.includes(value as InventoryMovement['movementType'])) {
    return value as InventoryMovement['movementType'];
  }
  reportDtoAnomaly(entity, 'movementType', value);
  return 'ADJUSTMENT';
};

function normalizeQuantityReportBucket(raw: unknown, entity: string): QuantityReportBucket {
  const record = asRecord(raw);
  if (!record) reportDtoAnomaly(entity, 'root', raw);
  const quantityUnit = normalizeOptionalQuantityUnit(record?.quantityUnit, entity);
  return {
    quantity: asNumber(record?.quantity),
    ...(quantityUnit ? { quantityUnit } : {}),
    ...(record?.quantityPcs == null ? {} : { quantityPcs: asNumber(record.quantityPcs) }),
    ...(record?.quantityM2 == null ? {} : { quantityM2: asNumber(record.quantityM2) }),
    ...(record?.quantityM3 == null ? {} : { quantityM3: asNumber(record.quantityM3) }),
    valuePLN: asNumber(record?.valuePLN),
    ...(record?.count == null ? {} : { count: asNumber(record.count) }),
  };
}

export function normalizeMonthlyInventoryReport(
  raw: unknown,
  entity = 'monthlyInventoryReport',
): MonthlyInventoryReport {
  const record = asRecord(raw);
  if (!record) reportDtoAnomaly(entity, 'root', raw);
  const summary = asRecord(record?.summary);
  const variants = asOptionalArray(record?.variants, (item, index) => {
    const variant = asRecord(item);
    if (!variant) reportDtoAnomaly(`${entity}.variants[${index}]`, 'root', item);
    const variantEntity = `${entity}.variants[${index}]`;
    return {
      productVariantId: asString(variant?.productVariantId),
      sku: asOptionalString(variant?.sku) ?? null,
      productName: asOptionalString(variant?.productName) ?? null,
      variantName: asOptionalString(variant?.variantName) ?? null,
      opening: normalizeQuantityReportBucket(variant?.opening, `${variantEntity}.opening`),
      receipts: normalizeQuantityReportBucket(variant?.receipts, `${variantEntity}.receipts`),
      releases: normalizeQuantityReportBucket(variant?.releases, `${variantEntity}.releases`),
      adjustments: normalizeQuantityReportBucket(variant?.adjustments, `${variantEntity}.adjustments`),
      closing: normalizeQuantityReportBucket(variant?.closing, `${variantEntity}.closing`),
    };
  });

  return {
    year: asNumber(record?.year),
    month: asNumber(record?.month),
    ...(asOptionalString(record?.periodStart) ? { periodStart: asOptionalString(record?.periodStart) } : {}),
    ...(asOptionalString(record?.periodEnd) ? { periodEnd: asOptionalString(record?.periodEnd) } : {}),
    summary: {
      openingBalancePLN: asNumber(summary?.openingBalancePLN),
      receiptsPLN: asNumber(summary?.receiptsPLN),
      releasesPLN: asNumber(summary?.releasesPLN),
      adjustmentsPLN: asNumber(summary?.adjustmentsPLN),
      closingBalancePLN: asNumber(summary?.closingBalancePLN),
      ...(summary?.variantsCount == null ? {} : { variantsCount: asNumber(summary.variantsCount) }),
    },
    variants: variants ?? [],
  };
}

export function normalizeInventoryBatch(raw: unknown, entity = 'inventoryBatch'): InventoryBatch {
  const record = asRecord(raw);
  if (!record) reportDtoAnomaly(entity, 'root', raw);

  const pzRecord = asRecord(record?.pzDocument);
  const pzId = asOptionalString(pzRecord?.id);
  const pzNumber = asOptionalString(pzRecord?.pzNumber);
  const quantityUnit = normalizeOptionalQuantityUnit(record?.quantityUnit, entity);

  return {
    id: asString(record?.id),
    productVariantId: asString(record?.productVariantId),
    batchNumber: asString(record?.batchNumber),
    pzDocumentId: asString(record?.pzDocumentId),
    ...(quantityUnit ? { quantityUnit } : {}),
    quantityReceived: asNumber(record?.quantityReceived),
    ...(record?.quantityReceivedPcs == null ? {} : { quantityReceivedPcs: asNumber(record.quantityReceivedPcs) }),
    ...(record?.quantityReceivedM2 == null ? {} : { quantityReceivedM2: asNumber(record.quantityReceivedM2) }),
    ...(record?.quantityReceivedM3 == null ? {} : { quantityReceivedM3: asNumber(record.quantityReceivedM3) }),
    quantityRemaining: asNumber(record?.quantityRemaining),
    ...(record?.quantityRemainingPcs == null ? {} : { quantityRemainingPcs: asNumber(record.quantityRemainingPcs) }),
    ...(record?.quantityRemainingM2 == null ? {} : { quantityRemainingM2: asNumber(record.quantityRemainingM2) }),
    ...(record?.quantityRemainingM3 == null ? {} : { quantityRemainingM3: asNumber(record.quantityRemainingM3) }),
    quantityReserved: asNumber(record?.quantityReserved),
    ...(record?.quantityReservedPcs == null ? {} : { quantityReservedPcs: asNumber(record.quantityReservedPcs) }),
    ...(record?.quantityReservedM2 == null ? {} : { quantityReservedM2: asNumber(record.quantityReservedM2) }),
    ...(record?.quantityReservedM3 == null ? {} : { quantityReservedM3: asNumber(record.quantityReservedM3) }),
    ...(record?.unitCostPLN == null ? {} : { unitCostPLN: asNumber(record.unitCostPLN) }),
    ...(record?.totalCostPLN == null ? {} : { totalCostPLN: asNumber(record.totalCostPLN) }),
    receiptDate: asString(record?.receiptDate),
    purchaseDate: asString(record?.purchaseDate),
    status: normalizeBatchStatus(record?.status, entity),
    ...(record?.freelyAvailable == null ? {} : { freelyAvailable: asNumber(record.freelyAvailable) }),
    ...(record?.freelyAvailablePcs == null ? {} : { freelyAvailablePcs: asNumber(record.freelyAvailablePcs) }),
    ...(record?.freelyAvailableM2 == null ? {} : { freelyAvailableM2: asNumber(record.freelyAvailableM2) }),
    ...(record?.freelyAvailableM3 == null ? {} : { freelyAvailableM3: asNumber(record.freelyAvailableM3) }),
    ...(pzId && pzNumber ? {
      pzDocument: {
        id: pzId,
        pzNumber,
        ...(asOptionalString(pzRecord?.receiptDate)
          ? { receiptDate: asOptionalString(pzRecord?.receiptDate) }
          : {}),
      },
    } : {}),
  };
}

export function normalizeInventoryMovement(
  raw: unknown,
  entity = 'inventoryMovement',
): InventoryMovement {
  const record = asRecord(raw);
  if (!record) reportDtoAnomaly(entity, 'root', raw);
  const quantityUnit = normalizeOptionalQuantityUnit(record?.quantityUnit, entity);

  return {
    id: asString(record?.id),
    productVariantId: asString(record?.productVariantId),
    movementType: normalizeMovementType(record?.movementType, entity),
    ...(quantityUnit ? { quantityUnit } : {}),
    quantity: asNumber(record?.quantity),
    ...(record?.quantityPcs == null ? {} : { quantityPcs: asNumber(record.quantityPcs) }),
    ...(record?.quantityM2 == null ? {} : { quantityM2: asNumber(record.quantityM2) }),
    ...(record?.quantityM3 == null ? {} : { quantityM3: asNumber(record.quantityM3) }),
    quantityBefore: asNumber(record?.quantityBefore),
    ...(record?.quantityBeforePcs == null ? {} : { quantityBeforePcs: asNumber(record.quantityBeforePcs) }),
    ...(record?.quantityBeforeM2 == null ? {} : { quantityBeforeM2: asNumber(record.quantityBeforeM2) }),
    ...(record?.quantityBeforeM3 == null ? {} : { quantityBeforeM3: asNumber(record.quantityBeforeM3) }),
    quantityAfter: asNumber(record?.quantityAfter),
    ...(record?.quantityAfterPcs == null ? {} : { quantityAfterPcs: asNumber(record.quantityAfterPcs) }),
    ...(record?.quantityAfterM2 == null ? {} : { quantityAfterM2: asNumber(record.quantityAfterM2) }),
    ...(record?.quantityAfterM3 == null ? {} : { quantityAfterM3: asNumber(record.quantityAfterM3) }),
    ...(record?.unitCostPLN == null ? {} : { unitCostPLN: asNumber(record.unitCostPLN) }),
    ...(record?.totalCostPLN == null ? {} : { totalCostPLN: asNumber(record.totalCostPLN) }),
    referenceNumber: asOptionalString(record?.referenceNumber),
    notes: asOptionalString(record?.notes),
    movementDate: asString(record?.movementDate),
    performedBy: asOptionalString(record?.performedBy),
  };
}

export function normalizeInventoryVariantDetail(
  raw: unknown,
  entity = 'inventoryVariantDetail',
): InventoryVariantDetailDto {
  const record = asRecord(raw);
  if (!record) reportDtoAnomaly(entity, 'root', raw);

  const batches = asOptionalArray(
    record?.batches,
    (item, index) => normalizeInventoryBatch(item, `${entity}.batches[${index}]`),
  );
  const recentMovements = asOptionalArray(
    record?.recentMovements,
    (item, index) => normalizeInventoryMovement(item, `${entity}.recentMovements[${index}]`),
  );

  return {
    batches: batches ?? [],
    recentMovements: recentMovements ?? [],
    batchesLoaded: batches !== undefined,
    recentMovementsLoaded: recentMovements !== undefined,
  };
}
